import { useEffect, useState } from 'react';

import { VIMEO, YOUTUBE } from '@xing-com/crate-jobs-constants';
import { useViewerData } from '@xing-com/hub';

type Consent = {
  hasConsent: boolean;
};

type ThirdPartyContentConsentResult = {
  loading: boolean;
  hasConsent?: boolean;
  userCentrics: {
    isLoading: boolean;
    youtube?: Consent;
    vimeo?: Consent;
  };
};

const getConsent = async (vendorId: string): Promise<boolean> => {
  const consentDetails = await window.__ucCmp?.getConsentDetails();
  return consentDetails?.services[vendorId].consent.given ?? false;
};

export const useExternalContentConsent = (): ThirdPartyContentConsentResult => {
  const [consent, setConsent] = useState<{ youtube: boolean; vimeo: boolean }>({
    youtube: false,
    vimeo: false,
  });
  const [loadingConsent, setLoadingConsent] = useState(false);
  const viewerData = useViewerData();

  useEffect(() => {
    const updateConsent = async (): Promise<void> => {
      setLoadingConsent(true);
      try {
        const youtubeConsent = await getConsent(YOUTUBE);
        const vimeoConsent = await getConsent(VIMEO);
        setConsent({ youtube: youtubeConsent, vimeo: vimeoConsent });
      } catch {
        setLoadingConsent(false);
      }
      setLoadingConsent(false);
    };

    updateConsent();
    window.addEventListener('UC_UI_INITIALIZED', updateConsent);
    window.addEventListener('UC_CONSENT', updateConsent);
    window.addEventListener('UC_UI_CMP_EVENT', updateConsent);

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', updateConsent);
      window.removeEventListener('UC_CONSENT', updateConsent);
      window.removeEventListener('UC_UI_CMP_EVENT', updateConsent);
    };
  }, []);

  const isLoggedOut = viewerData.data?.loginState === 'LOGGED_OUT';
  const hasConsent =
    isLoggedOut || viewerData.data?.hasThirdPartyTrackingConsent;

  return {
    loading: viewerData.loading,
    hasConsent,
    userCentrics: {
      isLoading: loadingConsent,
      youtube: { hasConsent: consent.youtube },
      vimeo: { hasConsent: consent.vimeo },
    },
  };
};
